'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import { Calendar, Clock, TrendingUp, Search, Filter, Eye, Heart, ArrowRight, BookOpen } from 'lucide-react';
import { blogPosts, categories, sortOptions, type BlogPost } from '../../data/blog-posts';
import { getMultipleViews } from '../../lib/analytics';

export default function BlogPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('views');
  const [realTimeViews, setRealTimeViews] = useState<Record<string, { views: number; likes: number }>>({});


  // Fetch real-time view counts
  useEffect(() => {
    const postIds = blogPosts.map(post => post.id);
    getMultipleViews(postIds).then(views => {
      setRealTimeViews(views);
    }).catch(error => {
      console.error('Failed to fetch view counts:', error);
      setRealTimeViews({});
    });
  }, []);

  const filteredAndSortedPosts = useMemo(() => {
    let filtered = blogPosts.filter(post => {
      const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    // Sort posts based on selected criteria
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'views':
          const aViews = realTimeViews[a.id]?.views || a.views;
          const bViews = realTimeViews[b.id]?.views || b.views;
          return bViews - aViews;
        case 'likes':
          const aLikes = realTimeViews[a.id]?.likes || a.likes;
          const bLikes = realTimeViews[b.id]?.likes || b.likes;
          return bLikes - aLikes;
        case 'date':
          return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
        case 'featured':
          return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
        case 'readTime':
          return a.readTime - b.readTime;
        default:
          return 0;
      }
    });

    return filtered;
  }, [searchTerm, selectedCategory, sortBy, realTimeViews]);

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Our Blog
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          Discover insights, tutorials, and best practices from our team of experts.
        </p>
      </div>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <TrendingUp className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-400" />
            Featured Posts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredPosts.map(post => (
              <FeaturedPostCard key={post.id} post={post} realTimeViews={realTimeViews} />
            ))}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            All Posts ({filteredAndSortedPosts.length})
          </h2>
        </div>

        {filteredAndSortedPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">No posts found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedPosts.map(post => (
              <BlogPostCard key={post.id} post={post} realTimeViews={realTimeViews} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturedPostCard({ post, realTimeViews }: { post: BlogPost; realTimeViews: Record<string, { views: number; likes: number }> }) {
  return (
    <div className="group relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-xl overflow-hidden border-2 border-blue-500 dark:border-blue-400 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      {/* Content */}
      <div className="relative p-6">
        {/* Badges */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
              <TrendingUp className="w-3 h-3 mr-1 inline" />
              Featured
            </span>
            <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium px-2 py-1 rounded-full">
              {post.category}
            </span>
          </div>
        </div>

        {/* Title */}
        <Link href={`/blog/${post.id}`}>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200 line-clamp-2">
            {post.title}
          </h3>
        </Link>

        {/* Excerpt */}
        <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2 text-sm leading-relaxed">
          {post.excerpt}
        </p>

        {/* Meta Info */}
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
          <div className="flex items-center space-x-4">
            <span className="flex items-center bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg">
              <Calendar className="w-4 h-4 mr-1" />
              {new Date(post.publishDate).toLocaleDateString('en-US')}
            </span>
            <span className="flex items-center bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg">
              <BookOpen className="w-4 h-4 mr-1" />
              {post.readTime} min
            </span>
          </div>
        </div>

        {/* Tags and Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            {post.tags.slice(0, 2).map(tag => (
              <span key={tag} className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium px-3 py-1 rounded-full border border-gray-300 dark:border-gray-600">
                #{tag}
              </span>
            ))}
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Eye className="w-4 h-4 mr-1" />
              <span className="font-medium">{realTimeViews[post.id]?.views || 0}</span>
            </div>
            <div className="flex items-center text-sm text-red-500 dark:text-red-400">
              <Heart className="w-4 h-4 mr-1" />
              <span className="font-medium">{realTimeViews[post.id]?.likes || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BlogPostCard({ post, realTimeViews }: { post: BlogPost; realTimeViews: Record<string, { views: number; likes: number }> }) {
  return (
    <div className="group relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-gray-200 dark:border-gray-700">
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/3 to-purple-500/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      {/* Content */}
      <div className="relative p-6">
        {/* Badges */}
        <div className="flex items-center justify-between mb-4">
          <span className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 text-xs font-bold px-3 py-1 rounded-full">
            {post.category}
          </span>
          {post.featured && (
            <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
              <TrendingUp className="w-3 h-3 mr-1 inline" />
              Featured
            </span>
          )}
        </div>

        {/* Title */}
        <Link href={`/blog/${post.id}`}>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200 line-clamp-2">
            {post.title}
          </h3>
        </Link>

        {/* Excerpt */}
        <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm line-clamp-3 leading-relaxed">
          {post.excerpt}
        </p>

        {/* Meta Info */}
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
          <div className="flex items-center space-x-3">
            <span className="flex items-center bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg">
              <Calendar className="w-3 h-3 mr-1" />
              {new Date(post.publishDate).toLocaleDateString('en-US')}
            </span>
            <span className="flex items-center bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg">
              <BookOpen className="w-3 h-3 mr-1" />
              {post.readTime} min
            </span>
          </div>
        </div>

        {/* Tags and Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            {post.tags.slice(0, 2).map(tag => (
              <span key={tag} className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium px-3 py-1 rounded-full border border-gray-300 dark:border-gray-600">
                #{tag}
              </span>
            ))}
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Eye className="w-3 h-3 mr-1" />
              <span className="font-medium">{realTimeViews[post.id]?.views || 0}</span>
            </div>
            <div className="flex items-center text-sm text-red-500 dark:text-red-400">
              <Heart className="w-3 h-3 mr-1" />
              <span className="font-medium">{realTimeViews[post.id]?.likes || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
